import { camelize } from 'casing'
import { PlanType } from '@labrav/react-components'
import { DiscountType, ScheduleType } from '../../../types/flyout'
import type { CloudMarketplace } from '../../../types/index'
import type { Product, ProductDimension } from '../../productsListing/types'
import type { ResellerInfo } from '../../../types/resaleAuthorization'
import type {
  MarketplaceFieldMapping,
  MarketplaceTemplateData,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../types/mappingManager'
import {
  getDimensionsFromReferralMapping,
  getUpdatedDimensions,
  mergeMappedDimensionsWithProductDimensions,
} from '../../privateOffer/privateOfferCreation/utils/dimensionHelper'
import {
  getMetadataFromReferralMapping,
  toFieldMetadataEntry,
} from '../../privateOffer/privateOfferCreation/utils/mappingManagerHelper'
import { getPlansFromReferralMapping } from '../../privateOffer/privateOfferCreation/utils/microsoftPlanHelper'
import { installmentRowToFormKeySnake } from '../../privateOffer/privateOfferCreation/utils/installmentHelper'

export const mappingReferralObjectToForm = (
  productId: string,
  referralObject: Record<string, unknown> | undefined | null,
  dimensions?: Record<string, unknown>[] | null,
  mappingTemplate?: MarketplaceTemplateData,
  product?: Product,
  cloud?: CloudMarketplace,
  resellers?: ResellerInfo[]
): Record<string, unknown> => {
  if (!referralObject) {
    return {}
  }

  const fieldMappings = mappingTemplate?.templateData?.fieldMappings
  const out: Record<string, unknown> = {
    ...getOfferDetailInfo(referralObject, cloud, product),
  }

  const metaData = getMetadataFromReferralMapping(
    referralObject,
    fieldMappings?.metadata?.metadataMapping
  )
  if (metaData) {
    out.metaData = metaData
  }

  if (cloud === 'AWS' || !cloud) {
    const resellersInfo =
      resellers?.filter(
        reseller =>
          referralObject?.Labra__Reseller_Aws_Account_Id__c ===
          reseller.resellerAwsAccountId
      ) || []
    out.resellersInfo = resellersInfo.length > 0 ? resellersInfo : []

    const mappedDimensions = getDimensionsFromReferralMapping(
      productId,
      dimensions,
      fieldMappings,
      product?.productDimensions,
      product,
      true
    )
    const dimensionsList = (
      product
        ? getUpdatedDimensions(
            product?.productDimensions || [],
            product?.productType
          )
        : []
    ) as ProductDimension[]

    const mergedDimensions = mergeMappedDimensionsWithProductDimensions(
      mappedDimensions,
      dimensionsList
    )
    if (mergedDimensions) {
      out.dimensions = mergedDimensions
    }
  } else if (cloud === 'AZURE') {
    if (
      referralObject?.Labra__Reseller_Account_Id__c ||
      referralObject?.Labra__Reseller_Name__c
    ) {
      const resellerDetails = [
        {
          partnerName: referralObject?.Labra__Reseller_Name__c || '',
          id: referralObject?.Labra__Reseller_Account_Id__c || '',
        },
      ]
      out.resellerDetails = resellerDetails
    }

    if (referralObject?.Labra__End_Date__c) {
      out.offerExpirationDate = referralObject?.Labra__End_Date__c
    }

    const mappedPlans = getPlansFromReferralMapping(
      productId,
      dimensions,
      fieldMappings,
      product?.plans
    )
    if (mappedPlans) {
      out.plans =
        mappedPlans?.length > 0
          ? mappedPlans
          : [
              {
                displayName: '',
                planId: '',
                offerPricingType: 'saasNewCustomizedPlans',
                description: '',
                labraPlanId: '',
                discountPercentage: null,
                pricing: {
                  recurrentPrice: {
                    recurrentPriceMode: '',
                    priceInputOption: 'usd',
                    prices: [],
                  },
                },
              },
            ]
    }
  }

  return out
}

export const awsReferralFieldToFormMetadataKey: Record<string, string> = {
  Labra__Authorization_Description__c: 'agreement_description',
  Labra__Authorization_Duration__c: 'agreement_duration_type',
  Labra__Authorization_Name__c: 'agreement_name',
  Labra__Company_Name__c: 'company_name',
  Labra__Buyer_Account_ID__c: 'buyer_aws_account_id',
  Labra__Custom_Email_Message__c: 'email_custom_message',
  Labra__End_Date__c: 'agreement_expiration_date',
  Labra__Maximum_Allowed_Start_Date__c: 'max_agreement_start_date',
  Labra__Renewal_Type__c: 'renewal_channel',
  Labra__User_License_agreement__c: 'end_customer_eula_type',
  Labra__Reseller_Aws_Account_Id__c: 'reseller_aws_account_id',
  Labra__Duration__c: 'contract_duration_in_months_for_end_customer',
  Labra__EULA__c: 'end_customer_eula_files',
  Labra__Reseller_Agreement__c: 'reseller_license_agreement_files',
}

/** @deprecated Use awsReferralFieldToFormMetadataKey */
export const referralFieldToFormMetadataKey = awsReferralFieldToFormMetadataKey

export const azureReferralFieldToFormMetadataKey: Record<string, string> = {
  Labra__MSFT_Billing_Account_ID__c: 'buyer_billing_account_id',
  Labra__Company_Name__c: 'buyer_name',
  Labra__Custom_Email_Message__c: 'email_custom_message',
  Labra__EULA_Attachment__c: 'eula_files',
  Labra__EULA_Type__c: 'eula_type',
  Labra__Offer_Expiration_Date__c: 'offer_expiration_date',
  Labra__End_Date__c: 'offer_expiration_date',
  Labra__Offer_Name__c: 'mppo_name',
  Labra__Description__c: 'mppo_description',
  Labra__Renewal_Type__c: 'existing_contract_renewal',
  Labra__Reseller_Account_Id__c: 'reseller_account_id',
  Labra__Reseller_Name__c: 'reseller_name',
  Labra__Stakeholders__c: 'stakeholders',
  Labra__Metadata_Hide__c: 'metadata',
}

const awsTopLevelTemplateFieldMetadata: Array<{
  templateKey: keyof MarketplaceTemplatesDataFieldMappings
  formKey: string
}> = [
  { templateKey: 'awsAccountNumber', formKey: 'buyer_aws_account_id' },
  { templateKey: 'companyName', formKey: 'company_name' },
  { templateKey: 'recipient', formKey: 'agreement_recipients' },
  { templateKey: 'customEmailMessage', formKey: 'email_custom_message' },
  { templateKey: 'contractDuration', formKey: 'agreement_duration_type' },
  { templateKey: 'renewal', formKey: 'renewal' },
  { templateKey: 'eula', formKey: 'end_customer_eula_type' },
  { templateKey: 'customEula', formKey: 'end_customer_eula_files' },
]

const azureTopLevelTemplateFieldMetadata: Array<{
  templateKey: keyof MarketplaceTemplatesDataFieldMappings
  formKey: string
}> = [
  { templateKey: 'billingAccountId', formKey: 'buyer_billing_account_id' },
  { templateKey: 'companyName', formKey: 'buyer_name' },
  { templateKey: 'customEmailMessage', formKey: 'email_custom_message' },
  { templateKey: 'eula', formKey: 'eula_type' },
  { templateKey: 'customEula', formKey: 'eula_files' },
  { templateKey: 'offerExpirationDate', formKey: 'accept_by' },
  {
    templateKey: 'existingContractRenewal',
    formKey: 'existing_contract_renewal',
  },
  { templateKey: 'recipient', formKey: 'offer_recipients' },
]

export const getOfferDetailInfo = (
  referralObject: Record<string, unknown>,
  cloud?: CloudMarketplace,
  product?: Product
): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  const pickString = (sfField: string, formKey: string) => {
    const v = referralObject[sfField]
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      out[formKey] = v
    }
  }

  if (cloud === 'AZURE') {
    pickString('Labra__MSFT_Billing_Account_ID__c', 'beneficiary_id')
    pickString('Labra__Company_Name__c', 'company_name')
    pickString('Labra__Offer_Name__c', 'mppo_name')
    pickString('Labra__Description__c', 'mppo_description')
    pickString('Labra__EULA_Type__c', 'eula_type')
    pickString('Labra__EULA_Attachment__c', 'eula_files')
    pickString('Labra__Custom_Email_Message__c', 'email_custom_message')
    pickString('Labra__Offer_Expiration_Date__c', 'accept_by')
    pickString('Labra__Renewal_Type__c', 'existing_contract_renewal')
    pickString('Labra__End_Date__c', 'offer_expiration_date')
    out.existing_contract_renewal =
      referralObject?.Labra__Renewal_Type__c === 'Yes'
  } else {
    pickString('Labra__Authorization_Description__c', 'agreement_description')
    pickString('Labra__Authorization_Name__c', 'agreement_name')
    pickString('Labra__Buyer_Account_ID__c', 'buyer_aws_account_id')
    pickString('Labra__Company_Name__c', 'company_name')
    pickString('Labra__Custom_Email_Message__c', 'email_custom_message')
    pickString('Labra__End_Date__c', 'agreement_expiration_date')
    pickString(
      'Labra__Maximum_Allowed_Start_Date__c',
      'max_agreement_start_date'
    )
    pickString('Labra__EULA__c', 'end_customer_eula_files')
    pickString(
      'Labra__Reseller_Agreement__c',
      'reseller_license_agreement_files'
    )

    if (referralObject?.Labra__Authorization_Duration__c) {
      out.agreement_duration_type = (
        referralObject?.Labra__Authorization_Duration__c as string
      )?.toUpperCase()
    }
    if (referralObject?.Labra__User_License_agreement__c) {
      out.end_customer_eula_type = (
        referralObject?.Labra__User_License_agreement__c as string
      )?.toUpperCase()
    }

    if (referralObject?.Labra__Reseller_Agreement__c) {
      out.reseller_license_agreement_type = 'CUSTOM'
    }
    const contractMonthsRaw = referralObject.Labra__Duration__c
    if (
      contractMonthsRaw !== undefined &&
      contractMonthsRaw !== null &&
      String(contractMonthsRaw).trim() !== ''
    ) {
      const n = Number(contractMonthsRaw)
      if (!Number.isNaN(n)) {
        out.contract_duration_in_months_for_end_customer = n
      }
    }
    if (referralObject?.Labra__Subscription_Start_Date__c) {
      out.variableStartDate = false
    }
    const isFlexiblePaymentTerms =
      !!referralObject?.Labra__Installment_Amount__c ||
      !!referralObject?.Labra__Installment_Date__c
    if (isFlexiblePaymentTerms) {
      out.installmentInfo = [
        {
          amount: referralObject?.Labra__Installment_Amount__c,
          paymentDate: referralObject?.Labra__Installment_Date__c,
        },
      ]
      out.firstInstallmentDate = referralObject?.Labra__Installment_Date__c
      out.discountType = DiscountType.FLEXIBLE_PAYMENT_TERMS
      out.flexiblePaymentType = ScheduleType.CUSTOM
    }

    const isSaaSSubscription =
      product?.productType === PlanType.SAAS_SUBSCRIPTION
    if (isSaaSSubscription) {
      out.discountType = DiscountType.USAGE_PRICING
    }

    const isRenewal = !!referralObject?.Labra__Renewal_Type__c
    if (isRenewal) {
      out.renewal = true
      out.renewalChannel = referralObject?.Labra__Renewal_Type__c
    }
  }

  return {
    ...out,
  }
}

type FieldMappingFlags = { isHidden?: boolean; isReadonly?: boolean }

export const getFieldMetadataFromTemplate = (
  template: MarketplaceTemplatesDataFieldMappings | undefined | null,
  _referralObject?: Record<string, unknown>,
  cloud?: CloudMarketplace
): Record<string, unknown> => {
  const out: Record<string, unknown> = {}

  const assign = (
    formKey: string | undefined,
    field?: FieldMappingFlags | null
  ) => {
    if (!formKey) {
      return
    }
    out[formKey] = toFieldMetadataEntry(field)
  }

  if (!template) {
    return camelize(out) as Record<string, unknown>
  }

  const topLevelTemplateFieldMetadata =
    cloud === 'AZURE'
      ? azureTopLevelTemplateFieldMetadata
      : awsTopLevelTemplateFieldMetadata

  for (const { templateKey, formKey } of topLevelTemplateFieldMetadata) {
    const field = template[templateKey] as MarketplaceFieldMapping | undefined
    if (field && typeof field === 'object') {
      assign(formKey, field)
    }
  }

  const fieldsMapping = template.fields?.fieldsMapping
  if (fieldsMapping) {
    for (const mapping of Object.values(fieldsMapping)) {
      const referralField =
        typeof mapping?.referralField === 'string' ? mapping.referralField : ''
      const formKey =
        cloud === 'AZURE'
          ? azureReferralFieldToFormMetadataKey[referralField]
          : awsReferralFieldToFormMetadataKey[referralField]
      if (formKey) {
        assign(formKey, mapping)
      }
    }
  }

  const metadataMapping = template.metadata?.metadataMapping
  if (metadataMapping) {
    const metadata = Object.values(metadataMapping).map(meta => {
      return {
        key: meta.key ?? '',
        value: meta.defaultValue ?? '',
        hidden: meta.isHidden,
        readOnly: meta.isReadonly,
      }
    })
    out.metadata = metadata
  }

  if (cloud !== 'AZURE') {
    const installmentsMapping = template.installments?.installmentsMapping
    if (installmentsMapping) {
      for (const [installmentKey, inst] of Object.entries(
        installmentsMapping
      )) {
        assign(installmentRowToFormKeySnake(inst, installmentKey), inst)
      }
    }
  }

  const recipientSourceMapping = (_referralObject?.Labra__Recipient_Source__c ||
    []) as Record<string, unknown>[]
  if (recipientSourceMapping.length > 0) {
    const recipients = recipientSourceMapping.map(recipient => ({
      firstName: recipient?.FirstName,
      lastName: recipient?.LastName,
      email: recipient?.email,
      title: recipient?.Title,
    }))
    if (cloud === 'AZURE') {
      out.offerRecipientsList = {
        offerRecipients: recipients,
      }
    } else {
      out.agreementRecipientsList = {
        agreementRecipients: recipients,
      }
    }
  }

  return camelize(out) as Record<string, unknown>
}
